import { FormEvent, useMemo } from 'react';
import { Loader2 } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import SelectField from '../../../components/Forms/Input/SelectField';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';

import { RestaurantInventoryDTO } from '../../../types/restaurant/RestaurantInventoryTypes';
import { RestaurantWasteReason } from '../../../types/restaurant/RestaurantCatalogExtensionTypes';
import { RestaurantWasteForm } from '../../../hooks/restaurant/useRestaurantWaste';

import { restaurantWasteReasonOptions } from './restaurantWasteUtils';

interface WasteFormModalProps {
  isOpen: boolean;
  form: RestaurantWasteForm;
  inventory: RestaurantInventoryDTO[];
  selectedInventory?: RestaurantInventoryDTO;
  saving: boolean;
  onClose: () => void;
  onChange: <K extends keyof RestaurantWasteForm>(
    field: K,
    value: RestaurantWasteForm[K]
  ) => void;
  onSave: () => void;
}

const inputClassName =
  'mt-2 w-full rounded-xl border border-stroke bg-transparent px-4 py-3 text-sm text-black outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-strokedark dark:bg-meta-4 dark:text-white';

const WasteFormModal = ({
  isOpen,
  form,
  inventory,
  selectedInventory,
  saving,
  onClose,
  onChange,
  onSave,
}: WasteFormModalProps) => {
  const errors = useMemo(() => {
    const next: Record<string, string> = {};

    if (!form.inventoryId) next.inventoryId = 'Seleccione una materia prima.';
    if (Number(form.quantity) <= 0)
      next.quantity = 'La cantidad debe ser mayor que cero.';
    if (!form.occurredAt) next.occurredAt = 'La fecha y hora es obligatoria.';

    return next;
  }, [form]);

  const hasErrors = !!Object.keys(errors).length;

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!hasErrors) {
      onSave();
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title="Registrar merma"
      maxWidth="max-w-xl"
      zIndex="top"
      headerExtra={<RestaurantFormHelp topic="waste" />}
    >
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-bodydark2 dark:text-bodydark">
          El movimiento se aplicará inmediatamente al inventario.
        </p>

        <SelectField
          label="Materia prima"
          name="wasteInventory"
          value={form.inventoryId}
          iconKey="product"
          required
          error={errors.inventoryId}
          options={inventory.map((item) => ({
            value: item.id,
            label: `${item.rawMaterialName} · ${item.currentStock} ${item.unitCode}`,
          }))}
          onChange={(event) =>
            onChange('inventoryId', Number(event.target.value))
          }
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm font-medium text-black dark:text-white">
            Cantidad
            <input
              required
              min="0.000001"
              step="any"
              type="number"
              value={form.quantity}
              onChange={(event) => onChange('quantity', event.target.value)}
              className={inputClassName}
            />
            {errors.quantity && (
              <p className="mt-2 text-sm text-danger">{errors.quantity}</p>
            )}
          </label>

          <label className="block text-sm font-medium text-black dark:text-white">
            Fecha y hora
            <input
              required
              type="datetime-local"
              value={form.occurredAt}
              onChange={(event) => onChange('occurredAt', event.target.value)}
              className={inputClassName}
            />
            {errors.occurredAt && (
              <p className="mt-2 text-sm text-danger">{errors.occurredAt}</p>
            )}
          </label>
        </div>

        <SelectField
          label="Motivo"
          name="wasteReason"
          value={form.reason}
          iconKey="status"
          options={restaurantWasteReasonOptions}
          onChange={(event) =>
            onChange('reason', event.target.value as RestaurantWasteReason)
          }
        />

        <label className="block text-sm font-medium text-black dark:text-white">
          Notas
          <textarea
            rows={3}
            value={form.notes}
            onChange={(event) => onChange('notes', event.target.value)}
            className={`${inputClassName} resize-none`}
          />
        </label>

        {selectedInventory && (
          <p className="rounded-xl border border-warning/20 bg-warning/10 p-3 text-sm font-medium text-warning">
            Existencia actual: {selectedInventory.currentStock}{' '}
            {selectedInventory.unitCode}
          </p>
        )}

        <div className="flex justify-end gap-3 border-t border-stroke pt-5 dark:border-strokedark">
          <div className="w-full sm:w-auto">
            <ActionButton
              label="Cancelar"
              variant="secondary"
              size="md"
              fullWidth={false}
              disabled={saving}
              onClick={onClose}
            />
          </div>

          <div className="w-full sm:w-auto">
            <button
              type="submit"
              disabled={saving || hasErrors}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Registrando...' : 'Registrar merma'}
            </button>
          </div>
        </div>
      </form>
    </FormModal>
  );
};

export default WasteFormModal;
